import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function PasswordResetPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; document.head.appendChild(meta); }
    meta.content = 'noindex, follow';
    return () => { if (meta) meta.content = 'index, follow'; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API}/api/auth/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setSent(true);
    } catch {
      toast.error('Fehler beim Senden');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-6">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
            <Building2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-semibold text-lg tracking-tight">VerwalterHonorar.de</span>
        </div>
        <Card>
          <CardHeader className="text-center pb-4">
            <div className="mx-auto h-10 w-10 rounded-full bg-secondary flex items-center justify-center mb-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
            </div>
            <CardTitle className="text-lg">Passwort zurücksetzen</CardTitle>
            <CardDescription>Geben Sie Ihre E-Mail-Adresse ein.</CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="text-center space-y-4" data-testid="reset-sent">
                <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Falls ein Konto mit dieser E-Mail existiert, wurde ein Link zum Zurücksetzen gesendet. Bitte prüfen Sie Ihr Postfach.
                </p>
                <Link to="/login">
                  <Button variant="outline" className="gap-2 mt-2" data-testid="back-to-login-btn">
                    <ArrowLeft className="h-4 w-4" /> Zurück zur Anmeldung
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">E-Mail-Adresse</Label>
                  <Input id="reset-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@firma.de" required autoFocus data-testid="reset-email-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading || !email} data-testid="reset-submit-btn">
                  {loading ? 'Senden...' : 'Link zum Zurücksetzen senden'}
                </Button>
                <p className="text-center text-sm text-muted-foreground">
                  <Link to="/login" className="text-primary hover:underline font-medium">Zurück zur Anmeldung</Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
